import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router";
import { Box, CircularProgress, Typography, Button, IconButton, useMediaQuery } from "@mui/material";
import DashboardHeader from "../../components/dashboard-header";
import { DesignerPremiumProfile, FavoriteProject } from "../../components/designer-premium-profile";
import { useGetDesignerBioQuery } from "../../hooks/use-get-designer-bio";
import { useGetDesignerProjectFiles } from "../../hooks/use-get-designer-project-files";
import { useGetDesignerMatchingProfileQuery } from "../../hooks/use-get-designer-matching-profile";
import { useGetAllDesignerAccountsQuery } from "../../hooks/useGetAllDesignerAccountsQuery";
import { useGenerateUserAlias } from "../../hooks/useGenerateUserAlias";
import { useEffectiveRole } from "../../hooks/useEffectiveRole";
import { useUserContext } from "../../hooks/userContext";
import { useNotification } from "../../contexts/NotificationContext";
import { usePresence } from "../../contexts/PresenceContext";
import { Pages } from "../../utils";
import { PopulatedProjectFile } from "../../__generated__/types";
import FloatingChatWidget from "../../components/floating-chat-widget";
import {
  projectCategoryLabels,
  projectStyleLabels,
  formatSpecialRequirementLabel,
  bugetRangeLabels,
  projectDurationLabels,
  squareFootageLabels,
} from "../../utils/option-helpers";
import CloseIcon from "@mui/icons-material/Close";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import "./styles.scss";
import {
  fetchMatchReaction,
  fetchMessagePermission,
  fetchPresenceStatuses,
  fetchDesignerStats,
  postMatchReaction,
  type MatchReaction,
} from "../../services/matchesApi";
import { fetchDesignerProjects } from "../../services/collaborationApi";
import { formatDistance } from "../matches-page/utils";
import {
  MATCH_REACTION_EVENT,
  broadcastMatchReactionUpdate,
  getMatchReactionStorageKey,
  readMatchReactionOverrides,
  type MatchReactionOverrideMap,
} from "../../utils/match-reaction-cache";
import {
  getDesignerAliasVariants,
  normalizeDesignerAliasId,
} from "../../utils/designer-alias";

type DesignerProfileState = {
  loading: boolean;
  error?: string | null;
};

const isValidObjectId = (value: string | null | undefined) => {
  if (!value) return false;
  return /^[a-f\d]{24}$/i.test(value);
};

const getFeaturedProjects = (
  projects: PopulatedProjectFile[] | undefined
): FavoriteProject[] => {
  if (!projects) return [];
  return projects
    .filter(
      (project) =>
        Boolean(project?.featuredOnBio) && project?.published !== false
    )
    .map((project) => {
      const projectTypeLabel = project.projectCategory
        ? projectCategoryLabels[project.projectCategory] ?? project.projectCategory
        : undefined;
      const projectStyleLabel = project.projectStyle
        ? projectStyleLabels[project.projectStyle] ?? project.projectStyle
        : undefined;
      const specialRequirements = (project.specialRequirements ?? [])
        .map((requirement) =>
          requirement ? formatSpecialRequirementLabel(requirement) : null
        )
        .filter((value): value is string => Boolean(value));
      const budgetRangeLabel = project.budgetRange
        ? bugetRangeLabels[project.budgetRange] ?? project.budgetRange
        : undefined;
      const projectDurationLabel = project.projectDuration
        ? projectDurationLabels[project.projectDuration] ?? project.projectDuration
        : undefined;
      const squareFootageLabel = project.squareFootage
        ? squareFootageLabels[project.squareFootage] ?? project.squareFootage
        : undefined;
      const gallery = (project.images ?? [])
        .map((image, index) => ({
          id: image?._id ?? `${project._id}-img-${index}`,
          url: image?.url ?? undefined,
        }))
        .filter((image) => Boolean(image.url));

      return {
        id: project._id,
        title: project.name || "Untitled Project",
        imageUrl: project.images?.[0]?.url || undefined,
        description: project.description || undefined,
        projectTypeLabel,
        projectStyleLabel,
        specialRequirements,
        budgetRangeLabel,
        projectDurationLabel,
        squareFootageLabel,
        gallery,
      };
    });
};

const fallbackText = (value?: string | null) =>
  value && value.trim() !== "" ? value : undefined;

const normalizeReaction = (
  reaction: MatchReaction | null | undefined
): MatchReaction | null => {
  if (reaction === "love_like") return "love";
  return reaction ?? null;
};

const hasLoveReaction = (reaction: MatchReaction | null | undefined) =>
  normalizeReaction(reaction) === "love";

const hasLikeReaction = (reaction: MatchReaction | null | undefined) =>
  normalizeReaction(reaction) === "like";

const getNextReaction = (
  current: MatchReaction | null,
  next: MatchReaction
): MatchReaction => {
  const normalizedCurrent = normalizeReaction(current);
  if (next === null) return null;
  if (next === "not_for_me") {
    return normalizedCurrent === "not_for_me" ? null : "not_for_me";
  }
  if (next === "love") {
    return normalizedCurrent === "love" ? null : "love";
  }
  if (next === "like") {
    return normalizedCurrent === "like" ? null : "like";
  }
  if (next === "love_like") {
    return "love";
  }
  return normalizedCurrent ?? null;
};

const getReactionOverride = (
  candidates: Array<string | null | undefined>,
  overrides: MatchReactionOverrideMap
) => {
  for (const candidate of candidates) {
    if (!candidate) continue;
    if (Object.prototype.hasOwnProperty.call(overrides, candidate)) {
      return overrides[candidate] ?? null;
    }
  }
  return undefined;
};

type DesignerProfileRouteState = {
  topMatchTags?: string[];
  matchScore?: number | null;
  distanceMiles?: number | null;
  locationLabel?: string;
};

export const DesignerPremiumProfilePage = () => {
  const { designerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const profileState = location.state as DesignerProfileRouteState | null;
  const { user } = useUserContext();
  const routeDesignerAliasVariants = useMemo(
    () => new Set(getDesignerAliasVariants(designerId ?? null)),
    [designerId]
  );
  const matchStateKey = useMemo(() => {
    if (!user?.id || !designerId) return null;
    return `designer-profile-match:${user.id}:${designerId}`;
  }, [designerId, user?.id]);
  const [cachedMatchState, setCachedMatchState] = useState<{
    topMatchTags?: string[];
    matchScore?: number | null;
    distanceMiles?: number | null;
    locationLabel?: string;
  } | null>(null);
  const [designerStats, setDesignerStats] = useState<{
    matches: number | null;
    projects: number | null;
  }>({ matches: null, projects: null });
  const [state, setState] = useState<DesignerProfileState>({ loading: true });
  const { isDesigner, isHomeowner } = useEffectiveRole();
  const { showError } = useNotification();
  const { getPresenceStatus } = usePresence();
  const isMobile = useMediaQuery("(max-width: 900px)");

  const { getDesignerBio } = useGetDesignerBioQuery();
  const { getDesignerProjectFiles } = useGetDesignerProjectFiles();
  const { getDesignerMatchingProfile } = useGetDesignerMatchingProfileQuery();
  const { getAllDesignerAccounts } = useGetAllDesignerAccountsQuery();

  const [bioData, setBioData] = useState<any>(null);
  const [projectsData, setProjectsData] = useState<PopulatedProjectFile[]>([]);
  const [matchingProfile, setMatchingProfile] = useState<any>(null);
  const [designerAccount, setDesignerAccount] = useState<any>(null);
  const [reaction, setReaction] = useState<MatchReaction | null>(null);
  const [contactPermission, setContactPermission] = useState<{
    allowed: boolean;
    reason: string | null;
  }>({ allowed: true, reason: null });
  const [designerPresenceFallback, setDesignerPresenceFallback] = useState<
    boolean | null
  >(null);
  const { generateUserAlias } = useGenerateUserAlias();
  const chatOpenRef = useRef<(() => void) | null>(null);

  const handleProfileBack = useCallback(() => {
    if (isHomeowner) {
      navigate(Pages.MATCHES);
      return;
    }
    navigate(-1);
  }, [isHomeowner, navigate]);

  useEffect(() => {
    if (!isMobile) return;
    document.body.classList.add("designer-profile-mobile");
    return () => {
      document.body.classList.remove("designer-profile-mobile");
    };
  }, [isMobile]);

  useEffect(() => {
    if (!matchStateKey) {
      setCachedMatchState(null);
      return;
    }

    const hasIncomingScore =
      profileState?.matchScore !== null && profileState?.matchScore !== undefined;
    const hasIncomingDistance = typeof profileState?.distanceMiles === "number";
    const hasIncomingTags =
      Array.isArray(profileState?.topMatchTags) &&
      (profileState?.topMatchTags?.length ?? 0) > 0;
    const hasIncomingLocation =
      typeof profileState?.locationLabel === "string" &&
      profileState.locationLabel.trim().length > 0;
    const hasIncoming =
      Boolean(profileState) &&
      (hasIncomingScore ||
        hasIncomingTags ||
        hasIncomingDistance ||
        hasIncomingLocation);

    if (hasIncoming) {
      const payload = {
        matchScore:
          profileState?.matchScore !== undefined ? profileState.matchScore : null,
        topMatchTags: Array.isArray(profileState?.topMatchTags)
          ? profileState?.topMatchTags
          : undefined,
        distanceMiles:
          profileState?.distanceMiles !== undefined
            ? profileState.distanceMiles
            : undefined,
        locationLabel:
          typeof profileState?.locationLabel === "string" &&
          profileState.locationLabel.trim().length > 0
            ? profileState.locationLabel.trim()
            : undefined,
      };
      setCachedMatchState(payload);
      try {
        sessionStorage.setItem(matchStateKey, JSON.stringify(payload));
      } catch {
        // Ignore storage errors (private mode, etc.)
      }
      return;
    }

    try {
      const raw = sessionStorage.getItem(matchStateKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        matchScore?: number | null;
        topMatchTags?: string[];
        distanceMiles?: number | null;
        locationLabel?: string;
      };
      setCachedMatchState(parsed);
    } catch {
      // Ignore malformed cache
    }
  }, [matchStateKey, profileState]);

  const resolvedDesignerId = useMemo(() => {
    if (isValidObjectId(designerId ?? null)) {
      return designerId as string;
    }
    const accountId =
      designerAccount?.designerId?._id || designerAccount?._id || null;
    if (isValidObjectId(accountId)) return accountId;
    const bioDesignerId = (bioData?.designerId as any)?._id || null;
    if (isValidObjectId(bioDesignerId)) return bioDesignerId;
    return null;
  }, [bioData?.designerId, designerAccount, designerId]);

  const realtimeDesignerPresence = useMemo(() => {
    if (!resolvedDesignerId) return undefined;
    return getPresenceStatus(resolvedDesignerId, "Designer");
  }, [getPresenceStatus, resolvedDesignerId]);

  const isDesignerOnline =
    realtimeDesignerPresence ?? designerPresenceFallback ?? false;

  useEffect(() => {
    if (!resolvedDesignerId) {
      setDesignerPresenceFallback(null);
      return;
    }

    if (realtimeDesignerPresence !== undefined) {
      setDesignerPresenceFallback(realtimeDesignerPresence);
      return;
    }

    let cancelled = false;
    fetchPresenceStatuses([resolvedDesignerId], "Designer")
      .then((presence) => {
        if (cancelled) return;
        const status = presence?.[resolvedDesignerId];
        setDesignerPresenceFallback(
          typeof status === "boolean" ? status : null
        );
      })
      .catch(() => {
        if (cancelled) return;
        setDesignerPresenceFallback(null);
      });

    return () => {
      cancelled = true;
    };
  }, [resolvedDesignerId, realtimeDesignerPresence]);

  useEffect(() => {
    if (!resolvedDesignerId) {
      setDesignerStats({ matches: null, projects: null });
      return;
    }
    let cancelled = false;
    const loadStats = async () => {
      try {
        const [statsResult, projectsResult] = await Promise.allSettled([
          fetchDesignerStats(resolvedDesignerId),
          fetchDesignerProjects(resolvedDesignerId),
        ]);
        if (cancelled) return;

        const statsData =
          statsResult.status === "fulfilled" ? statsResult.value : null;
        const projectsData =
          projectsResult.status === "fulfilled"
            ? projectsResult.value?.projects
            : null;

        const matchesWon =
          Array.isArray(projectsData)
            ? projectsData.filter((project) => project.status !== "cancelled").length
            : null;

        setDesignerStats({
          matches:
            typeof matchesWon === "number"
              ? matchesWon
              : typeof statsData?.matches === "number"
                ? statsData.matches
                : null,
          projects:
            typeof statsData?.projects === "number" ? statsData.projects : null,
        });
      } catch {
        if (cancelled) return;
        setDesignerStats({ matches: null, projects: null });
      }
    };
    loadStats();
    return () => {
      cancelled = true;
    };
  }, [resolvedDesignerId]);

  useEffect(() => {
    if (!isHomeowner || !user?.id || !designerId) {
      setContactPermission({ allowed: true, reason: null });
      return;
    }
    let cancelled = false;
    fetchMessagePermission(user.id, "Homeowner", designerId, "Designer")
      .then((permission) => {
        if (cancelled) return;
        setContactPermission({
          allowed: permission?.allowed !== false,
          reason: permission?.reason ?? null,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setContactPermission({
          allowed: false,
          reason: "Unable to verify messaging permission right now. Please try again.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [designerId, isHomeowner, reaction, user?.id]);

  useEffect(() => {
    const fetchData = async () => {
      if (!designerId) {
        setState({ loading: false, error: "A designer identifier is required." });
        return;
      }

      setState({ loading: true, error: undefined });
      try {
        const [
          bioDataResult,
          projectFilesDataResult,
          matchingProfileDataResult,
          allDesignerAccountsData,
        ] = await Promise.all([
          getDesignerBio(designerId, true),
          getDesignerProjectFiles(designerId, true),
          getDesignerMatchingProfile(designerId, true),
          getAllDesignerAccounts(true),
        ]);

        setBioData(bioDataResult || null);
        setProjectsData(projectFilesDataResult || []);
        setMatchingProfile(matchingProfileDataResult || null);

        const accounts = allDesignerAccountsData;
        if (accounts && Array.isArray(accounts)) {
          const accountMatch = accounts.find((account: any) => {
            const accountDesignerId = account?.designerId?._id || account?._id;
            if (accountDesignerId === designerId) return true;
            const aliasId = account?.designerId?.aliasId;
            return aliasId ? routeDesignerAliasVariants.has(aliasId) : false;
          });
          setDesignerAccount(accountMatch ?? null);
        }

        setState({ loading: false });
      } catch (error) {
        setState({
          loading: false,
          error: "We were unable to load this designer profile. Please try again later.",
        });
      }
    };

    fetchData();
  }, [
    designerId,
    getDesignerBio,
    getDesignerProjectFiles,
    getDesignerMatchingProfile,
    getAllDesignerAccounts,
    routeDesignerAliasVariants,
  ]);

  useEffect(() => {
    if (!isHomeowner || !user?.id || !designerId) return;
    let cancelled = false;
    const overrides = readMatchReactionOverrides(user?.id);
    const override = getReactionOverride(
      [
        designerId,
        designerAccount?.designerId?._id,
        designerAccount?.designerId?.aliasId,
        designerAccount?.aliasId,
      ],
      overrides
    );
    if (override !== undefined) {
      setReaction(override);
      return;
    }
    fetchMatchReaction(user.id, designerId)
      .then((storedReaction) => {
        if (!cancelled) {
          setReaction(storedReaction);
        }
      })
      .catch(() => {
        if (!cancelled) {
          showError("Unable to load reaction state.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    designerAccount?.aliasId,
    designerAccount?.designerId?._id,
    designerAccount?.designerId?.aliasId,
    designerId,
    isHomeowner,
    showError,
    user?.id,
  ]);

  useEffect(() => {
    if (!designerId) return;
    const handleReactionEvent = (event: Event) => {
      const detail = (event as CustomEvent<{
        profileId?: string;
        reaction?: MatchReaction;
      }>).detail;
      if (!detail?.profileId) return;
      const candidates = [
        designerId,
        designerAccount?.designerId?._id,
        designerAccount?.designerId?.aliasId,
        designerAccount?.aliasId,
      ].filter(Boolean) as string[];
      if (!candidates.includes(detail.profileId)) return;
      setReaction(detail.reaction ?? null);
    };

    const handleStorage = (event: StorageEvent) => {
      const storageKey = getMatchReactionStorageKey(user?.id);
      if (!storageKey || event.key !== storageKey) return;
      const overrides = readMatchReactionOverrides(user?.id);
      const override = getReactionOverride(
        [
          designerId,
          designerAccount?.designerId?._id,
          designerAccount?.designerId?.aliasId,
          designerAccount?.aliasId,
        ],
        overrides
      );
      if (override !== undefined) {
        setReaction(override);
      }
    };

    window.addEventListener(MATCH_REACTION_EVENT, handleReactionEvent as EventListener);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(MATCH_REACTION_EVENT, handleReactionEvent as EventListener);
      window.removeEventListener("storage", handleStorage);
    };
  }, [
    designerAccount?.aliasId,
    designerAccount?.designerId?._id,
    designerAccount?.designerId?.aliasId,
    designerId,
    user?.id,
  ]);

  // Alias generation logic
  useEffect(() => {
    const generateAliasIfNeeded = async () => {
      if (designerAccount?.designerId?._id && !designerAccount?.designerId?.aliasId) {
        try {
          await generateUserAlias(designerAccount.designerId._id, 'designer');
        } catch (error) {
          console.error('Failed to generate alias:', error);
        }
      }
    };

    generateAliasIfNeeded();
  }, [designerAccount?.designerId?._id, designerAccount?.designerId?.aliasId, generateUserAlias]);

  const featuredProjects = useMemo(
    () => getFeaturedProjects(projectsData),
    [projectsData]
  );

  const matchingAttributes = useMemo(() => {
    const fromState = profileState?.topMatchTags ?? cachedMatchState?.topMatchTags;
    const normalizedStateTags = Array.isArray(fromState)
      ? fromState.filter((tag) => typeof tag === "string" && tag.trim()).slice(0, 3)
      : [];
    if (normalizedStateTags.length) return normalizedStateTags;

    const attributes = new Set<string>();
    (matchingProfile?.expertise?.projectTypes ?? []).forEach((item: string) =>
      attributes.add(item)
    );
    (matchingProfile?.style?.strongStyles ?? []).forEach((item: string) =>
      attributes.add(item)
    );
    return Array.from(attributes).slice(0, 3);
  }, [matchingProfile, profileState?.topMatchTags, cachedMatchState?.topMatchTags]);

  const compatibilityData = useMemo(() => {
    const stateScore = profileState?.matchScore ?? cachedMatchState?.matchScore;
    if (stateScore !== null && stateScore !== undefined) {
      const numericScore =
        typeof stateScore === "number" ? stateScore : Number(stateScore);
      if (Number.isFinite(numericScore)) {
        const percentage = Math.max(0, Math.min(100, numericScore));
        return {
          percentage,
          ratingValue: Math.round((percentage / 20) * 10) / 10,
          ratingScale: 5,
          label: "Compatibility Rating",
          description: "Smart-Guild Matching Overall Score",
        };
      }
    }

    // Fallback: calculate compatibility percentage based on matching profile completion
    const stepCompletion = matchingProfile?.stepCompletion;
    if (!stepCompletion) return null;

    const completedSteps = Object.values(stepCompletion).filter(Boolean).length;
    const totalSteps = Object.keys(stepCompletion).length;
    const percentage = Math.round((completedSteps / totalSteps) * 100);

    // Calculate rating based on years of experience and project types
    const yearsExperience = bioData?.yearsExperience || 0;
    const projectTypesCount = matchingProfile?.expertise?.projectTypes?.length || 0;
    const ratingValue = Math.min(5, Math.max(3, 3 + (yearsExperience / 10) + (projectTypesCount / 10)));

    return {
      percentage,
      ratingValue: Math.round(ratingValue * 10) / 10,
      ratingScale: 5,
      label: "Compatibility Rating",
      description: "Based on matching profile completion and experience",
    };
  }, [bioData, matchingProfile, profileState?.matchScore, cachedMatchState?.matchScore]);

  const distanceLabel = useMemo(() => {
    const distanceMiles = profileState?.distanceMiles ?? cachedMatchState?.distanceMiles;
    if (typeof distanceMiles !== "number") return undefined;
    return formatDistance(distanceMiles);
  }, [cachedMatchState?.distanceMiles, profileState?.distanceMiles]);
  const locationLabel = useMemo(() => {
    const fromMatchState = profileState?.locationLabel ?? cachedMatchState?.locationLabel;
    if (typeof fromMatchState === "string" && fromMatchState.trim().length > 0) {
      return fromMatchState.trim();
    }
    if (designerAccount?.designerId?.businessCity && designerAccount?.designerId?.businessState) {
      return `${designerAccount.designerId.businessCity}, ${designerAccount.designerId.businessState}`;
    }
    return undefined;
  }, [
    cachedMatchState?.locationLabel,
    designerAccount?.designerId?.businessCity,
    designerAccount?.designerId?.businessState,
    profileState?.locationLabel,
  ]);
  const isContactDisabled = isHomeowner && !contactPermission.allowed;

  const handleLearnMoreClick = useCallback(() => {
    if (!designerId) return;
    navigate(Pages.DESIGNER_COMPATIBILITY_DETAILS.replace(":designerId", designerId));
  }, [designerId, navigate]);

  const handleContactDesigner = useCallback(() => {
    if (isContactDisabled) {
      showError(
        contactPermission.reason || "Messaging is unavailable for this match."
      );
      return;
    }
    chatOpenRef.current?.();
  }, [contactPermission.reason, isContactDisabled, showError]);

  const handleReactionChange = useCallback(
    async (next: MatchReaction) => {
      if (!isHomeowner || !user?.id || !designerId) return;
      const current = reaction ?? null;
      const updated = getNextReaction(current, next);
      setReaction(updated);
      try {
        await postMatchReaction(designerId, updated, user.id);
        broadcastMatchReactionUpdate(designerId, updated, user?.id);
      } catch {
        showError("Unable to save your reaction. Please try again.");
        setReaction(current);
      }
    },
    [designerId, isHomeowner, reaction, showError, user?.id]
  );

  if (state.loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress sx={{ color: "#5C3BFF" }} />
      </Box>
    );
  }

  if (state.error) {
    return (
      <Box className="site-wrapper" sx={{ py: 6 }}>
        <Typography color="error" variant="body1">
          {state.error}
        </Typography>
        <Button sx={{ mt: 2 }} onClick={handleProfileBack}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (!bioData) {
    return (
      <Box className="site-wrapper" sx={{ py: 6 }}>
        <Typography variant="body1" color="text.secondary">
          This designer has not completed their bio yet.
        </Typography>
      </Box>
    );
  }

  const normalizedDesignerAliasId = normalizeDesignerAliasId(
    designerAccount?.designerId?.aliasId ||
      designerAccount?.aliasId
  );
  const designerName = normalizedDesignerAliasId || "Designer";
  const headerTitle =
    normalizedDesignerAliasId ||
    designerId ||
    "Designer";

  const experienceStats = [
    {
      label: "Years Active",
      value: bioData.yearsExperience ? `${bioData.yearsExperience}+` : undefined,
    },
    {
      label: "Matches",
      value:
        designerStats.matches === null || designerStats.matches === undefined
          ? undefined
          : `${designerStats.matches}`,
    },
    {
      label: "HDG Projects",
      value:
        designerStats.projects === null || designerStats.projects === undefined
          ? undefined
          : `${designerStats.projects}`,
    },
  ];

  const favoriteItems = [
    {
      label: "Favorite Book",
      value: fallbackText(bioData.favoriteBook),
    },
    {
      label: "Music Genre",
      value: fallbackText(bioData.favoriteMusicGenre),
    },
    {
      label: "Favorite Movie/Show",
      value: fallbackText(bioData.favoriteMovie),
    },
    {
      label: "Currently Reading/Listening",
      value: fallbackText(bioData.currentlyReading),
    },
  ];

  const mobileContactAction =
    isMobile && designerId ? (
      <FloatingChatWidget
        recipientId={designerId}
        recipientRole="Designer"
        recipientName={designerName}
        recipientAvatarUrl={bioData?.image?.url ?? null}
        onReady={(openChat) => {
          chatOpenRef.current = openChat;
        }}
        renderTrigger={(openChat) => (
          <button
            type="button"
            className="designer-premium-action-btn is-contact"
            aria-label="Contact"
            disabled={isContactDisabled}
            title={
              isContactDisabled
                ? contactPermission.reason || "Messaging is unavailable for this match."
                : undefined
            }
            onClick={openChat}
          >
            <ChatBubbleOutlineIcon />
          </button>
        )}
        isDisabled={isContactDisabled}
        disabledReason={contactPermission.reason}
      />
    ) : undefined;

  return (
    <Box className={isMobile ? "designer-profile-mobile-wrapper" : "site-wrapper"}>
      {isMobile ? (
        <Box className="designer-profile-mobile-header">
          <Typography className="designer-profile-mobile-title">
            {headerTitle}
          </Typography>
          <IconButton
            aria-label="Close profile"
            onClick={handleProfileBack}
            className="designer-profile-mobile-close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      ) : (
        <DashboardHeader
          title="Designer Profile"
          showBackButton={true}
          onBack={handleProfileBack}
        />
      )}

      <DesignerPremiumProfile
        designerName={designerName}
        designerId={designerId ?? null}
        isOnline={isDesignerOnline}
        aliasId={normalizedDesignerAliasId}
        locationLabel={locationLabel}
        distanceLabel={distanceLabel}
        avatarUrl={bioData.image?.url}
        coverImageUrl={
          typeof bioData.coverImage === "object" && bioData.coverImage?.url
            ? bioData.coverImage.url
            : bioData.image?.url || (featuredProjects.length > 0 ? featuredProjects[0].imageUrl : undefined)
        }
        coverImagePositionX={Number(bioData.coverImagePositionX ?? 0) || 0}
        coverImagePositionY={Number(bioData.coverImagePositionY ?? 0) || 0}
        compatibility={compatibilityData}
        topAttributes={matchingAttributes}
        experienceStats={experienceStats}
        aboutMe={bioData.aboutMe}
        certifications={bioData.education}
        favoriteProjects={featuredProjects}
        personalNote={bioData.whyWorkWithMe}
        designAdvice={bioData.designAdvice}
        favorites={favoriteItems}
        testimonials={bioData.reviews || []}
        onContactDesigner={handleContactDesigner}
        onCompatibilityLearnMore={handleLearnMoreClick}
        mobileContactAction={mobileContactAction}
        enableActions={isHomeowner}
        isViewingAsHomeowner={isHomeowner}
        isViewingAsDesigner={isDesigner}
        isContactDisabled={isContactDisabled}
        contactDisabledReason={contactPermission.reason}
        isLoved={hasLoveReaction(reaction)}
        isLiked={hasLikeReaction(reaction)}
        isDismissed={reaction === "not_for_me"}
        isFavorite={hasLoveReaction(reaction)}
        onLove={() => handleReactionChange("love")}
        onLike={() => handleReactionChange("like")}
        onDismiss={() => handleReactionChange("not_for_me")}
      />
      {!isMobile && designerId && (
        <FloatingChatWidget
          recipientId={designerId}
          recipientRole="Designer"
          recipientName={designerName}
          recipientAvatarUrl={bioData?.image?.url ?? null}
          isDisabled={isContactDisabled}
          disabledReason={contactPermission.reason}
          onReady={(openChat) => {
            chatOpenRef.current = openChat;
          }}
        />
      )}
    </Box>
  );
};
